import React, { useState, useEffect } from 'react';
import AuditLogger from '../../services/auditLogger';

const CostMonitor = ({ isDarkMode = false }) => {
  const [metrics, setMetrics] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const updateMetrics = () => {
      const currentMetrics = AuditLogger.getUsageMetrics();
      setMetrics(currentMetrics);
    };

    // Update immediately
    updateMetrics();

    // Update every 30 seconds
    const interval = setInterval(updateMetrics, 30000);

    return () => clearInterval(interval);
  }, []);

  if (!metrics) return null;

  const baseStyles = {
    container: `fixed bottom-4 right-4 z-50 ${
      isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
    } border rounded-lg shadow-lg transition-all duration-300`,
    header: `p-3 cursor-pointer flex items-center justify-between ${
      isDarkMode ? 'text-white hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-50'
    }`,
    content: `p-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`,
    metric: `flex justify-between items-center py-1 ${
      isDarkMode ? 'text-gray-300' : 'text-gray-600'
    }`,
    label: 'text-sm font-medium',
    value: 'text-sm font-mono',
    cost: 'text-green-600 font-semibold',
    warning: 'text-yellow-600 font-semibold',
    danger: 'text-red-600 font-semibold'
  };

  const getCostColor = (cost) => {
    if (cost > 0.50) return baseStyles.danger;
    if (cost > 0.10) return baseStyles.warning;
    return baseStyles.cost;
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className={baseStyles.container} style={{ width: isExpanded ? '300px' : '200px' }}>
      <div className={baseStyles.header} onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
          <span className="font-semibold text-sm">Firebase Usage</span>
        </div>
        <span className="text-xs">{isExpanded ? '−' : '+'}</span>
      </div>
      
      {isExpanded && (
        <div className={baseStyles.content}>
          <div className={baseStyles.metric}>
            <span className={baseStyles.label}>Daily Writes:</span>
            <span className={baseStyles.value}>{metrics.dailyWrites}</span>
          </div>
          
          <div className={baseStyles.metric}>
            <span className={baseStyles.label}>Daily Reads:</span>
            <span className={baseStyles.value}>{metrics.dailyReads}</span>
          </div>
          
          <div className={baseStyles.metric}>
            <span className={baseStyles.label}>Log Size:</span>
            <span className={baseStyles.value}>{formatSize(metrics.totalLogSize)}</span>
          </div>
          
          <div className="border-t pt-2 mt-2">
            <div className={baseStyles.metric}>
              <span className={baseStyles.label}>Daily Cost:</span>
              <span className={`${baseStyles.value} ${getCostColor(metrics.estimatedDailyCost)}`}>
                ${metrics.estimatedDailyCost.toFixed(6)}
              </span>
            </div>
            
            <div className={baseStyles.metric}>
              <span className={baseStyles.label}>Monthly Est:</span>
              <span className={`${baseStyles.value} ${getCostColor(metrics.estimatedMonthlyCost)}`}>
                ${metrics.estimatedMonthlyCost.toFixed(4)}
              </span>
            </div>
          </div>
          
          {metrics.estimatedMonthlyCost > 0.10 && (
            <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 rounded text-xs">
              <div className="font-semibold text-yellow-800 dark:text-yellow-200">
                Cost Alert
              </div>
              <div className="text-yellow-700 dark:text-yellow-300">
                Monthly cost estimate exceeds $0.10. Consider implementing log retention.
              </div>
            </div>
          )}
          
          <div className="mt-2 text-xs text-gray-500 text-center">
            Updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CostMonitor;
